import countries from 'shared/constants/countries';
import { AUTOMATIONS } from '../../automation/constants';

const operator = value => ({
  value,
  labelKey: `AUTOMATION.FLOW.OPERATORS.${value.toUpperCase()}`,
});

const OPERATORS_BY_TYPE = {
  text: [
    'equal_to',
    'not_equal_to',
    'contains',
    'does_not_contain',
    'starts_with',
    'is_present',
    'is_not_present',
  ].map(operator),
  number: [
    'equal_to',
    'not_equal_to',
    'is_greater_than',
    'is_less_than',
    'is_present',
    'is_not_present',
  ].map(operator),
  date: [
    'equal_to',
    'is_after',
    'is_before',
    'is_present',
    'is_not_present',
  ].map(operator),
  list: ['equal_to', 'not_equal_to', 'is_present', 'is_not_present'].map(
    operator
  ),
  boolean: ['is_true', 'is_false'].map(operator),
};

const VALUELESS_OPERATORS = [
  'is_present',
  'is_not_present',
  'is_true',
  'is_false',
];

export const operatorNeedsValue = value =>
  !!value && !VALUELESS_OPERATORS.includes(value);

const standard = (attribute, type, labelKey = attribute.toUpperCase()) => ({
  scope: 'standard',
  attribute,
  type,
  labelKey: `AUTOMATION.FLOW.FIELDS.${labelKey}`,
});

const STANDARD_FIELDS = {
  contact: [
    standard('name', 'text'),
    standard('email', 'text'),
    standard('phone_number', 'text'),
    standard('identifier', 'text'),
    standard('blocked', 'boolean'),
    standard('country_code', 'text', 'COUNTRY'),
    standard('state', 'text'),
    standard('city', 'text'),
    standard('zip_code', 'text'),
    standard('gender', 'text'),
    standard('birth_date', 'date'),
    standard('created_at', 'date'),
    standard('last_activity_at', 'date', 'LAST_ACTIVITY'),
  ],
  company: [
    standard('name', 'text'),
    standard('domain', 'text'),
    standard('email', 'text'),
    standard('phone_number', 'text'),
    standard('cnpj', 'text'),
    standard('city', 'text'),
    standard('country_code', 'text', 'COUNTRY'),
    standard('description', 'text'),
    standard('contacts_count', 'number', 'CONTACTS_COUNT'),
    standard('created_at', 'date'),
    standard('last_activity_at', 'date', 'LAST_ACTIVITY'),
  ],
  deal: [
    standard('name', 'text'),
    standard('value', 'number'),
    standard('currency', 'text'),
    standard('assigned_to_id', 'list', 'ASSIGNED_TO'),
    standard('pipeline_id', 'list', 'PIPELINE'),
    standard('pipeline_stage_id', 'list', 'STAGE'),
    standard('last_purchase_at', 'date', 'LAST_PURCHASE'),
    standard('created_at', 'date'),
  ],
};

const CUSTOM_ATTRIBUTE_TYPES = {
  text: 'text',
  link: 'text',
  number: 'number',
  currency: 'number',
  percent: 'number',
  date: 'date',
  list: 'list',
  multiple_choice: 'list',
  checkbox: 'boolean',
};

const SOURCES = ['contact', 'company', 'deal'];

export const encodeField = ({ source, scope, attribute }) =>
  source && attribute ? `${source}|${scope}|${attribute}` : '';

export const decodeField = value => {
  const [source, scope, attribute] = value.split('|');
  return { source, scope, attribute };
};

const withDefaults = (field, source) => ({
  ...field,
  source,
  id: encodeField({ ...field, source }),
  operators: OPERATORS_BY_TYPE[field.type],
});

export const toOptions = items =>
  items?.map(({ id, name }) => ({ value: String(id), label: name }));

const CURRENCIES = ['BRL', 'USD', 'EUR', 'GBP'].map(code => ({
  value: code,
  label: code,
}));

const resolveOptions = (field, { agents, pipelines }) => {
  if (field.attribute === 'country_code') return toOptions(countries);
  if (field.attribute === 'currency') return CURRENCIES;
  if (field.attribute === 'assigned_to_id') {
    return agents.map(({ id, name }) => ({ value: String(id), label: name }));
  }
  if (field.attribute === 'pipeline_id') {
    return pipelines.map(({ id, name }) => ({
      value: String(id),
      label: name,
    }));
  }
  if (field.attribute === 'pipeline_stage_id') {
    return pipelines.flatMap(pipeline =>
      (pipeline.stages || []).map(stage => ({
        value: String(stage.id),
        label: `${pipeline.name} / ${stage.name}`,
      }))
    );
  }
  return field.options;
};

// Eventos de negociação listam as condições de negociação, que já têm grupo próprio.
const conversationFields = (event, conditionOptions) =>
  (AUTOMATIONS[event]?.conditions || []).map(
    ({ key, name, filterOperators }) => ({
      id: encodeField({
        source: 'conversation',
        scope: 'standard',
        attribute: key,
      }),
      source: 'conversation',
      scope: 'standard',
      attribute: key,
      type: 'text',
      options: event.startsWith('deal_')
        ? undefined
        : toOptions(conditionOptions(key)),
      labelKey: `AUTOMATION.ATTRIBUTES.${name}`,
      operators: filterOperators.map(({ value }) => ({
        value,
        labelKey: `FILTER.OPERATOR_LABELS.${value}`,
      })),
    })
  );

const customFields = (definitions, source) =>
  definitions.map(definition => {
    const type =
      CUSTOM_ATTRIBUTE_TYPES[definition.attribute_display_type] || 'text';
    return withDefaults(
      {
        scope: 'custom',
        attribute: definition.attribute_key,
        type,
        label: definition.attribute_display_name,
        options: (definition.attribute_values || []).map(value => ({
          value: String(value),
          label: String(value),
        })),
      },
      source
    );
  });

// Grupos de campos disponíveis para as condições, na ordem em que são exibidos.
export const buildFieldGroups = ({
  event,
  customAttributes,
  agents,
  pipelines,
  conditionOptions,
}) => {
  const groups = [];
  const conversation = conversationFields(event, conditionOptions);
  if (conversation.length) {
    groups.push({
      id: 'conversation',
      labelKey: 'AUTOMATION.FLOW.SOURCES.CONVERSATION',
      fields: conversation,
    });
  }
  SOURCES.forEach(source => {
    const label = `AUTOMATION.FLOW.SOURCES.${source.toUpperCase()}`;
    groups.push({
      id: source,
      labelKey: label,
      fields: STANDARD_FIELDS[source].map(field => {
        const built = withDefaults(field, source);
        return {
          ...built,
          options: resolveOptions(built, { agents, pipelines }),
        };
      }),
    });
    const custom = customFields(customAttributes[source] || [], source);
    if (custom.length) {
      groups.push({
        id: `${source}_custom`,
        labelKey: `${label}_CUSTOM`,
        fields: custom,
      });
    }
  });
  return groups;
};

export const findField = (groups, rule) => {
  const id = encodeField(rule);
  return groups.flatMap(group => group.fields).find(field => field.id === id);
};

export const newRule = () => ({
  source: '',
  scope: 'standard',
  attribute: '',
  operator: '',
  value: '',
  join: 'and',
});

// Condições antigas guardavam um único campo no próprio nó.
export const normalizeConditionData = data => {
  if (Array.isArray(data.rules)) return data;
  return {
    ...data,
    rules: [
      {
        ...newRule(),
        source: data.attribute ? 'conversation' : '',
        attribute: data.attribute || '',
        operator: data.operator || '',
        value: data.value || '',
      },
    ],
  };
};

export const valueMatchesType = (type, value) => {
  if (type === 'number') return Number.isFinite(Number(value));
  if (type === 'date') {
    return (
      /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value))
    );
  }
  return true;
};

// Verdadeiro quando o valor precisa ser uma das opções conhecidas do campo.
export const hasFixedOptions = field =>
  Array.isArray(field.options) &&
  (field.options.length > 0 || field.scope === 'standard');
